import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Mail, Key } from "lucide-react";

import { useForm } from "react-hook-form";
import { useLogin } from "@/features/auth/hooks/useLogin";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { loginSchema } from "@/features/auth/schemas/loginSchema";
import { LoginData } from "@/features/auth/types";

const Auth: React.FC = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string>("");

  // Form hook with validation
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: "",
      password: "",
    },
  });

  // Login hook with error handling
  const { mutate: login, isPending, error: loginError } = useLogin();

  const onSubmit = (data: LoginData) => {
    setErrorMessage(""); // Clear previous errors

    login(data, {
      onError: (error) => {
        console.error('Login error:', error);
        setErrorMessage("Credenciales inválidas o error del servidor.");
      },
    });
  };

  // Show server error if exists
  if (loginError) {
    setErrorMessage("Error de conexión con el servidor.");
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#F1F0FB] to-[#E5DEFF] px-2">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader>
          <CardTitle className="text-center text-2xl font-semibold tracking-tight">
            Bienvenido
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <Label htmlFor="username" className="flex gap-2 items-center">
                <Mail size={16} />
                Correo electrónico
              </Label>
              <Input
                id="username"
                type="email"
                placeholder="tucorreo@email.com"
                autoComplete="email"
                disabled={isPending}
                {...register("username")}
              />
              {errors.username && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.username.message}
                </p>
              )}
            </div>
            
            <div>
              <Label htmlFor="password" className="flex gap-2 items-center">
                <Key size={16} />
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={isPending}
                {...register("password")}
              />
              {errors.password && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            {errorMessage && (
              <div className="text-sm text-red-500 text-center bg-red-50 p-2 rounded">
                {errorMessage}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full mt-4" 
              disabled={isPending}
            >
              {isPending ? "Iniciando..." : "Iniciar sesión"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
